import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaPlus } from "react-icons/fa";
import {
  fetchFetchedallplans,
  fetchFetchedallplanswithcurrentplanById,
  postAssignProjectPlan,
} from "../../api/customer.api";
import { useProjectPhone } from "../../hooks/useProjectPhone";
import PlanComparison, { planLabel } from "./PlanComparison";
import PlanFormModal from "./PlanFormModal";

const Plans = () => {
  const { projectId } = useProjectPhone();
  const [currentPlanName, setCurrentPlanName] = useState("");
  const [currentPlanId, setCurrentPlanId] = useState("");
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [plans, setPlans] = useState([]);
  const [editingPlan, setEditingPlan] = useState(undefined);
  const [refreshKey, setRefreshKey] = useState(0);
  const [updating, setUpdating] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const loadPlansAndCurrent = useCallback(async () => {
    if (!projectId) {
      setCurrentPlanName("");
      setCurrentPlanId("");
      setSelectedPlanId("");
      setPlans([]);
      return;
    }

    setError("");
    try {
      const [allRes, currentRes] = await Promise.all([
        fetchFetchedallplans(),
        fetchFetchedallplanswithcurrentplanById({ id: projectId }),
      ]);

      const list = Array.isArray(allRes)
        ? allRes
        : allRes?.data || currentRes?.data || [];
      setPlans(Array.isArray(list) ? list : []);

      const current =
        currentRes?.currentPlan || currentRes?.userPlanData || null;
      const name = planLabel(current);
      const id = String(current?._id || current?.id || "").trim();
      setCurrentPlanName(name !== "Plan" ? name : "");
      setCurrentPlanId(id);
      setSelectedPlanId(id);
    } catch (err) {
      setPlans([]);
      setCurrentPlanName("");
      setCurrentPlanId("");
      setSelectedPlanId("");
      setError(err.message || "Failed to load plans");
    }
  }, [projectId]);

  useEffect(() => {
    loadPlansAndCurrent();
  }, [loadPlansAndCurrent, refreshKey]);

  const planOptions = useMemo(
    () =>
      plans.map((plan) => ({
        id: String(plan._id || plan.id || ""),
        name: planLabel(plan),
      })),
    [plans],
  );

  const canUpdate =
    Boolean(projectId) &&
    Boolean(selectedPlanId) &&
    selectedPlanId !== currentPlanId &&
    !updating;

  const onUpdatePlan = async () => {
    if (!canUpdate) return;
    setUpdating(true);
    setStatus("");
    setError("");
    try {
      const res = await postAssignProjectPlan({
        projectId,
        planId: selectedPlanId,
      });
      const nextName =
        planLabel(res?.currentPlan) !== "Plan"
          ? planLabel(res.currentPlan)
          : planOptions.find((p) => p.id === selectedPlanId)?.name || "";
      setCurrentPlanId(selectedPlanId);
      setCurrentPlanName(nextName);
      setStatus(res?.message || "Plan updated successfully.");
      setRefreshKey((n) => n + 1);
    } catch (err) {
      setError(err.message || "Failed to update plan");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="biz-plans-page">
      <div className="biz-panel-header biz-plans-header">
        <div className="biz-plans-header-copy">
          <h1>Plans</h1>
          {currentPlanName ? (
            <p className="biz-plans-current">
              Current plan: <strong>{currentPlanName}</strong>
            </p>
          ) : (
            <p className="biz-plans-current">
              Select a plan and click Update to apply it to this project
            </p>
          )}
        </div>
        <div className="biz-plans-header-actions">
          <label className="biz-plans-select-wrap">
            <select
              className="biz-plans-select"
              value={selectedPlanId}
              onChange={(e) => setSelectedPlanId(e.target.value)}
              disabled={!projectId || planOptions.length === 0}
              aria-label="Select plan"
            >
              <option value="">Select plan</option>
              {planOptions.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="biz-btn-outline"
            onClick={onUpdatePlan}
            disabled={!canUpdate}
          >
            {updating ? "Updating…" : "Update"}
          </button>
          <button
            type="button"
            className="biz-btn-primary"
            onClick={() => setEditingPlan(null)}
          >
            <FaPlus /> Add plan
          </button>
        </div>
      </div>

      {status ? <p className="biz-banner biz-banner-success">{status}</p> : null}
      {error ? <p className="biz-banner biz-banner-error">{error}</p> : null}

      <div className="renew-plan-page">
        <PlanComparison
          projectId={projectId}
          currentPlanName={currentPlanName}
          title="Compare plans"
          managePlans
          refreshKey={refreshKey}
          onEdit={(plan) => setEditingPlan(plan)}
        />
      </div>
      {editingPlan !== undefined && (
        <PlanFormModal
          plan={editingPlan}
          onClose={() => setEditingPlan(undefined)}
          onSaved={() => {
            setEditingPlan(undefined);
            setRefreshKey((n) => n + 1);
          }}
        />
      )}
    </div>
  );
};

export default Plans;
